import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const languages = [
  { code: 'en', label: 'EN', path: '/en' },
  { code: 'fr', label: 'FR', path: '/fr' },
  { code: 'da', label: 'DA', path: '/da' },
  { code: 'de', label: 'DE', path: '/de' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const currentLang = i18n.language || 'en';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  const switchLang = (code: string) => {
    i18n.changeLanguage(code);
    const basePath = location.pathname.replace(/^\/(en|fr|da|de)/, '');
    navigate(`/${code}${basePath || '/'}`);
  };

  const navItems = [
    { label: t('navProducts'), href: '/products' },
    { label: t('navSpreadsheet'), href: '/spreadsheet' },
    { label: t('navReviews'), href: '/reviews' },
    { label: t('navShipping'), href: '/shipping' },
    { label: t('navCoupons'), href: '/coupons' },
    { label: t('navWarehouse'), href: '/warehouse' },
    { label: t('navQC'), href: '/qc' },
    { label: t('navGuides'), href: '/guides' },
    { label: t('navSafety'), href: '/safety' },
    { label: t('navBlog'), href: '/blog' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200' : 'bg-transparent'
      }`}
    >
      <div className="container-main">
        <nav className="flex items-center justify-between h-16 md:h-18">
          <Link to={`/${currentLang}`} className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 rounded-md flex items-center justify-center bg-primary-500">
              <i className="ri-table-line text-background-50 text-sm" />
            </div>
            <span className="font-heading font-bold text-lg md:text-xl text-foreground-950">
              {t('siteName')}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1 lg:gap-2">
            {navItems.map((item) => (
              <Link key={item.href} to={item.href} className="nav-link px-2 lg:px-3 py-2 rounded-md">
                {item.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setCatOpen(!catOpen)}
                className="flex items-center gap-1 nav-link px-3 py-2 rounded-md"
              >
                {t('navCategories')}
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} />
              </button>
              {catOpen && (
                <div className="dropdown-menu max-h-[400px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.href}
                      target="_blank"
                      rel="noopener nofollow"
                      className="dropdown-item"
                      onClick={() => setCatOpen(false)}
                    >
                      <span className="w-7 h-7 rounded-md flex items-center justify-center bg-primary-100">
                        <i className={`${cat.icon} text-primary-600 text-xs`} />
                      </span>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 nav-link px-3 py-2 rounded-md"
              >
                <i className="ri-global-line" />
                {currentLang.toUpperCase()}
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="dropdown-menu right-0 left-auto min-w-[100px]">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => switchLang(lang.code)}
                      className={`dropdown-item w-full text-left ${currentLang === lang.code ? 'text-primary-600 font-semibold' : ''}`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-primary text-xs py-2 px-4"
            >
              {t('ctaExplore')}
            </a>
          </div>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-700"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
          </button>
        </nav>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50/98 backdrop-blur-md max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="container-main py-4 space-y-1">
            {navItems.map((item) => (
              <Link key={item.href} to={item.href} className="flex items-center px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600">
                {item.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-background-200">
              <p className="px-3 py-2 text-xs font-medium text-foreground-400 uppercase tracking-wider">
                {t('navCategories')}
              </p>
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.href}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600"
                >
                  <i className={`${cat.icon} text-primary-500`} />
                  {cat.name}
                </a>
              ))}
            </div>
            <div className="pt-2 border-t border-background-200 flex gap-2 px-3">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => switchLang(lang.code)}
                  className={`lang-btn ${currentLang === lang.code ? 'lang-btn-active' : ''}`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
            <div className="pt-2 px-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="btn-primary w-full justify-center text-sm"
              >
                {t('ctaExplore')}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}